import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { IDashboardItems, IGlossary, IHyperlink, IReport, ITeaser, IUser, ReportStatus } from '@shared/interfaces';
import { GuardService } from '../../shared/services/guard.service';
import { Subscription } from 'rxjs';
import { UserService } from '../../shared/services/user.service';
import { CacheService } from '../../shared/services/cache.service';
import { ReportService } from '../../shared/services/report.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ModuleService } from '../../shared/services/module.service';
import { FaqService } from '@shared/services';
import { IS_PRODUCT_SAFETY_PROJECT, IS_TRS_PROJECT } from '../../../current-project';
import { environment } from '../../../environments/default/environment';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {
    IS_TRS_PROJECT = IS_TRS_PROJECT;
    IS_PRODUCT_SAFETY_PROJECT = IS_PRODUCT_SAFETY_PROJECT;

    env = environment;
    public teasers: ITeaser[] = [];

    public items: IDashboardItems;

    public currentUser: IUser;
    public currentLanguage = this.translateService.currentLang;
    subscription: Subscription[] = [];
    public selectedItem: { item: IHyperlink | IGlossary; teaser: ITeaser };
    public reportStatus = ReportStatus;

    constructor(
        private guardService: GuardService,
        private changeDetection: ChangeDetectorRef,
        private userService: UserService,
        private cache: CacheService,
        private reportService: ReportService,
        private router: Router,
        private translateService: TranslateService,
        private moduleService: ModuleService,
        public faqService: FaqService
    ) {}

    async ngOnInit() {
        this.initializeTeasers();

        if (IS_TRS_PROJECT) {
            document.getElementById('main-container').classList.add('no-padding');
        }

        await this.loadData();
        this.currentUser = this.guardService.currentUser;
        if (!this.currentUser) {
            await this.userService.getMyUser();
            this.currentUser = this.guardService.currentUser;
        }

        this.subscription.push(
            this.guardService.currentUserSubscribable.subscribe(e => {
                if (e) {
                    this.currentUser = e;
                }
            }),
            this.translateService.onLangChange.subscribe(e => (this.currentLanguage = e.lang))
        );
    }

    ngOnDestroy(): void {
        if (IS_TRS_PROJECT) {
            document.getElementById('main-container').classList.remove('no-padding');
        }
        this.subscription.forEach(e => e.unsubscribe());
    }

    private initializeTeasers(): void {
        if (IS_TRS_PROJECT) {
            this.teasers.push(
                {
                    format: 12,
                    image: '',
                    title: 'navigation.reports',
                    fontSizeHeader: 21,
                    backgroundColor: 'linear-gradient(90deg, #198cb9 0%, #2db4e1 52.7%, #0099B0 84.46%, #198cb9 100%)',
                    fontColorHeader: '#fff',
                    fontColorText: '#fff',
                    faIcon: 'file-alt',
                    headerLink: '/reports',
                    controlColor: '#196482',
                    type: 'reports',
                    hideIfEmpty: false,
                    isHidden: () => !this.moduleService.isReportModuleActive
                },
                {
                    isSplit: true,
                    items: [
                        {
                            format: 12,
                            image: '',
                            title: 'My Reporting Units',
                            fontSizeHeader: 21,
                            backgroundColor: '#196482',
                            fontColorHeader: '#50BED7',
                            fontColorText: '#50BED7',
                            faIcon: 'sitemap',
                            faIconColor: 'white',
                            headerLink: '/org-charts',
                            controlColor: '#196482',
                            type: 'reports',
                            hideIfEmpty: false,
                            isHidden: () => !this.moduleService.isOrganisationModuleActive
                        },
                        {
                            format: 12,
                            image: '',
                            title: 'navigation.faq',
                            fontSizeHeader: 21,
                            backgroundColor: '#196482',
                            fontColorHeader: '#50BED7',
                            fontColorText: '#50BED7',
                            faIcon: 'question-circle',
                            faIconColor: 'white',
                            controlColor: '#196482',
                            type: 'faq',
                            click: () => this.faqService.toggleFaq()
                        }
                    ]
                },
                {
                    format: 12,
                    image: '',
                    title: 'navigation.hyperlinks',
                    fontSizeHeader: 21,
                    backgroundColor: 'linear-gradient(90deg, #198cb9 0%, #2db4e1 52.7%, #0099B0 84.46%, #198cb9 100%)',
                    fontColorHeader: '#fff',
                    fontColorText: '#fff',
                    faIcon: 'link',
                    headerLink: '/links',
                    controlColor: '#50BED7',
                    type: 'hyperlinks',
                    hideIfEmpty: true,
                    isHidden: () => !this.moduleService.isHyperlinksModuleActive
                }
            );
        }

        if (IS_PRODUCT_SAFETY_PROJECT) {
            this.teasers.push(
                {
                    format: 12,
                    image: '',
                    title: 'navigation.reports',
                    fontSizeHeader: 21,
                    backgroundColor: 'linear-gradient(90deg, #198cb9 0%, #2db4e1 52.7%, #0099B0 84.46%, #198cb9 100%)',
                    fontColorHeader: '#fff',
                    fontColorText: '#fff',
                    faIcon: 'file-alt',
                    headerLink: '/reports',
                    controlColor: '#196482',
                    type: 'reports',
                    hideIfEmpty: false,
                    isHidden: () => !this.moduleService.isReportModuleActive
                },
                {
                    format: 12,
                    image: '',
                    title: 'navigation.glossary',
                    fontSizeHeader: 21,
                    backgroundColor: '#196482',
                    fontColorHeader: '#50BED7',
                    fontColorText: '#50BED7',
                    faIcon: 'book',
                    faIconColor: 'white',
                    headerLink: '/glossary',
                    controlColor: '#50BED7',
                    type: 'glossary',
                    hideIfEmpty: true,
                    isHidden: () => !this.moduleService.isGlossaryModuleActive
                },
                {
                    format: 12,
                    image: '',
                    title: 'navigation.hyperlinks',
                    fontSizeHeader: 21,
                    backgroundColor: '#196482',
                    fontColorHeader: '#50BED7',
                    fontColorText: '#50BED7',
                    faIcon: 'link',
                    faIconColor: 'white',
                    headerLink: '/links',
                    controlColor: '#50BED7',
                    type: 'hyperlinks',
                    hideIfEmpty: true,
                    isHidden: () => !this.moduleService.isHyperlinksModuleActive
                },
                {
                    format: 12,
                    image: '',
                    title: 'navigation.summaryReports',
                    fontSizeHeader: 21,
                    backgroundColor: 'linear-gradient(90deg, #198cb9 0%, #2db4e1 52.7%, #0099B0 84.46%, #198cb9 100%)',
                    fontColorHeader: '#fff',
                    fontColorText: '#fff',
                    faIcon: 'copy',
                    headerLink: '/summary-reports',
                    controlColor: '#196482',
                    type: 'summaries',
                    hideIfEmpty: false,
                    isHidden: () => !this.moduleService.isSummaryReportModuleActive
                }
            );
        }
    }

    public async loadData() {
        this.items = this.cache.homeItems;
        this.items = this.cache.homeItems = await this.userService.getHomeItems();
    }

    public async setItem(item: IReport) {
        this.cache.activeReport = await this.reportService.getItem(item.id);
        return this.router.navigateByUrl('report/' + item.id);
    }

    public keys(o: IDashboardItems) {
        return Object.keys(o);
    }

    public showGlossary(item: IGlossary | IHyperlink, teaser: ITeaser) {
        if (item === this.selectedItem?.item) {
            this.selectedItem = null;
        } else {
            this.selectedItem = {
                item,
                teaser: {
                    ...teaser,
                    faIcon: null,
                    title: this.currentLanguage === 'en' ? item.title : item.titleDe,
                    externalLink:
                        this.currentLanguage === 'en' ? (item as IHyperlink).urlLink : (item as IHyperlink).urlLinkDe
                }
            };
        }
    }

    isVisible(item: ITeaser, index: number) {
        if (item.isHidden && item.isHidden()) {
            return false;
        }

        if (!item.hideIfEmpty) {
            return true;
        }

        if (IS_TRS_PROJECT) {
            switch (item.type) {
                case 'glossary':
                    return this.items?.glossary?.length;
                case 'hyperlinks':
                    return this.items?.hyperlinks?.length;
                default:
                    return true;
            }
        }

        if (IS_PRODUCT_SAFETY_PROJECT) {
            switch (index) {
                case 1:
                    return this.items?.glossary?.length;
                case 2:
                    return this.items?.hyperlinks?.length;
                default:
                    return true;
            }
        }
    }

    public getMessageForUser(): { message: string; variable: string | undefined } {
        let message = '';
        let variable: string | undefined;
        if (this.items?.reports?.length) {
            const sortedReports = this.items?.reports.sort((a, b) => a.doc - b.doc);

            const responsibleReports = sortedReports.filter(
                r => this._isUserResponsibleForReport(r).isResponsible === true
            );
            const specialistReports = sortedReports.filter(
                r => this._isUserResponsibleForReport(r).isSpecialist === true
            );

            if (responsibleReports.length) {
                const firstReport = responsibleReports[0];
                if (firstReport.reportStatus === ReportStatus.inProgress) {
                    if (firstReport.doc < 0.95) {
                        message = 'generic.main.homeText.responsible-message.3';
                    } else if (firstReport.doc === 0.95) {
                        message = 'generic.main.homeText.responsible-message.2';
                    }
                } else if (firstReport.reportStatus === ReportStatus.released) {
                    if (firstReport.doc === 1) {
                        message = 'generic.main.homeText.responsible-message.1';
                    }
                }
            }
            if (specialistReports.length) {
                const firstReport = specialistReports[0];
                if (firstReport.doc >= 0) {
                    message = 'generic.main.homeText.specialist-message.1';
                }
                if (firstReport.doc > 0.1 && firstReport.doc < 0.9) {
                    message = 'generic.main.homeText.specialist-message.3';
                    const dueDate = new Date(firstReport.dueDate);
                    dueDate.setDate(dueDate.getDate() - 5);
                    variable = dueDate.toISOString();
                }
                if (firstReport.doc >= 0.9 && firstReport.doc < 0.95) {
                    message = 'generic.main.homeText.specialist-message.4';
                    const dueDate = new Date(firstReport.dueDate);
                    dueDate.setDate(dueDate.getDate() - 5);
                    variable = dueDate.toISOString();
                }
                if (firstReport.doc >= 0.95) {
                    message = '';
                }
            }
        }
        return { message, variable };
    }

    private _isUserResponsibleForReport(report: IReport): { isResponsible: boolean; isSpecialist: boolean } {
        return {
            isResponsible: !!report.responsible.responsible?.find(r => r.id === this.guardService.currentUser.id),
            isSpecialist: !!report.responsible.specialists?.find(r => r.id === this.guardService.currentUser.id)
        };
    }
}
